/**
 * Profile settings page — Personal Info + Authentication.
 *
 * Personal Info: initials avatar (no upload — deferred to Phase 2 with
 * Branding), display name with INLINE edit (click Edit → the name becomes
 * an input with Save/Cancel), read-only email.
 *
 * Authentication:
 *   - Email row (display only; "Manage on Adobe" style link deferred to Phase 2).
 *   - Change Password subsection (POST /api/auth/change-password — endpoint
 *     already exists, we just wire the UI).
 *   - Log Out button. Logs out THIS browser only (JWT cookie clear via existing
 *     POST /api/auth/logout). "All sessions" invalidation was scoped out
 *     2026-07-21 — see decision in project_workspace_settings_spec.
 */

import { useEffect, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Eye, EyeOff, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useAuth, useChangePassword, useLogout, useUpdateMe } from "@/hooks/use-auth";
import { ApiError } from "@/lib/api";

function initialsFrom(name: string | undefined | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ProfileSettingsPage() {
  const { user } = useAuth();

  return (
    <div>
      <SettingsPageHeader title="Profile" subtitle="Set up your profile." />

      <SectionLabel title="Personal Info" />
      <PersonalInfoCard
        name={user?.name || user?.email || ""}
        email={user?.email || ""}
      />

      <div className="mt-10">
        <SectionLabel title="Authentication" />
        <AuthenticationCards email={user?.email || ""} />
      </div>
    </div>
  );
}

// ── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ title }: { title: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
  );
}

// ── Personal Info ────────────────────────────────────────────────────────────

function PersonalInfoCard({ name, email }: { name: string; email: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateMe();

  // Keep draft in sync with server value when we're not editing.
  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  const startEdit = () => {
    setDraft(name);
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(name);
    setError(null);
  };
  const save = () => {
    const trimmed = draft.trim();
    if (trimmed.length < 1) {
      setError("Name can't be empty.");
      return;
    }
    if (trimmed.length > 120) {
      setError("Name is too long (max 120 characters).");
      return;
    }
    if (trimmed === name.trim()) {
      // No-op — just exit edit mode.
      setEditing(false);
      return;
    }
    update.mutate(
      { name: trimmed },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => {
          setError(err instanceof ApiError ? err.message : "Couldn't update your name.");
        },
      },
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-6">
      <div className="flex items-start gap-6">
        <div
          aria-hidden
          className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary/70 to-primary text-2xl font-semibold text-primary-foreground shadow-inner"
        >
          {initialsFrom(name)}
        </div>

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="max-w-md">
              <Input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); save(); }
                  if (e.key === "Escape") { e.preventDefault(); cancel(); }
                }}
                placeholder="Your name"
                className="h-10 text-lg"
                aria-label="Name"
              />
              <div className="mt-3 flex items-center gap-2">
                <Button
                  onClick={save}
                  disabled={update.isPending}
                  className="h-8"
                >
                  <Check className="mr-1.5 h-4 w-4" strokeWidth={2} />
                  {update.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={cancel}
                  disabled={update.isPending}
                  className="h-8"
                >
                  <X className="mr-1.5 h-4 w-4" strokeWidth={2} />
                  Cancel
                </Button>
              </div>
              {error && (
                <p className="mt-2 text-xs text-destructive">{error}</p>
              )}
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="truncate text-xl font-semibold text-foreground">
                  {name || "—"}
                </div>
                <div className="mt-1 truncate text-sm text-muted-foreground">
                  {email}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="shrink-0"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                Edit
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Authentication ───────────────────────────────────────────────────────────

function AuthenticationCards({ email }: { email: string }) {
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-3">
      {/* Email row — display only per current scope. */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">Email</div>
          <div className="truncate text-sm text-muted-foreground">{email}</div>
        </div>
        <span className="shrink-0 rounded-full border border-muted-foreground/30 bg-muted-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          Read-only
        </span>
      </div>

      {/* Change password subsection. */}
      <ChangePasswordCard />

      {/* Log out — this browser only. */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">Log Out</div>
          <div className="truncate text-sm text-muted-foreground">
            Sign out of this browser.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={logout.isPending}
          onClick={() =>
            logout.mutate(undefined, {
              onSettled: () => navigate("/login", { replace: true }),
            })
          }
        >
          {logout.isPending ? "Signing out…" : "Log Out"}
        </Button>
      </div>
    </div>
  );
}

// Password field with a show/hide eye toggle. Each field owns its own
// reveal state so users can independently peek at any of the three.
function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        aria-pressed={show}
        className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const change = useChangePassword();

  const reset = () => {
    setCurrent(""); setNew(""); setConfirm(""); setError(null); setSuccess(false);
  };

  const submit = () => {
    setError(null); setSuccess(false);
    if (!currentPassword) { setError("Current password is required."); return; }
    if (newPassword.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (newPassword !== confirm) { setError("New password and confirmation don't match."); return; }
    if (newPassword === currentPassword) { setError("New password must be different from the current one."); return; }
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setSuccess(true);
          setCurrent(""); setNew(""); setConfirm("");
        },
        onError: (err) => {
          setError(err instanceof ApiError ? err.message : "Couldn't change password.");
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-border bg-secondary/30">
      {/* Row header — click to expand/collapse. */}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (open) reset(); }}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">Change Password</div>
          <div className="truncate text-sm text-muted-foreground">
            Set a new password for your account.
          </div>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {open ? "Cancel" : "Change"}
        </span>
      </button>

      {open && (
        <div className="border-t border-border/60 px-4 py-4">
          <div className="grid max-w-md gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="current-password">Current password</Label>
              <PasswordInput
                id="current-password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-password">New password</Label>
              <PasswordInput
                id="new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNew(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Must be at least 8 characters.</p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <PasswordInput
                id="confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            {success && <p className="text-xs text-emerald-500">Password updated.</p>}
            <div className="mt-1 flex items-center gap-2">
              <Button onClick={submit} disabled={change.isPending} className="h-9">
                {change.isPending ? "Saving…" : "Save Password"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setOpen(false); reset(); }}
                disabled={change.isPending}
                className="h-9"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
